<template>
  <div class="settings-page">
    <h2>Account</h2>
    <div class="subtitle">Manage your account and your Routine experience.</div>

    <section>
      <h3>Sign-in</h3>
      <div class="entry">
        <label>Email</label>
        {{ email }}
      </div>
      <div v-if="signInMethod" class="entry">
        <label>Method</label>
        <span class="sign-in-method">
          <component :is="signInMethod.icon" />
          {{ signInMethod.label }}
        </span>
      </div>
    </section>

    <section>
      <h3>Application</h3>
      <div class="entry">
        <label>Version</label>
        <span class="version">
          <span @click="copyToClipboard(version)">{{ version }}</span>
          <CheckUpdatesButton compact />
        </span>
      </div>

      <div class="entry">
        <label>Channel</label>
        <Spinner v-if="channel === undefined" />
        <template v-else>{{ channel }}</template>
      </div>

      <div v-if="arch" class="entry">
        <label>Architecture</label>
        {{ arch }}
      </div>

      <div class="entry">
        <label>Timezone</label>
        {{ timezone }}
      </div>
    </section>

    <section>
      <h3>Dangerous</h3>
      <div class="entry">
        <div>
          <label>Delete account</label>
          <div class="help">
            Permanently delete your Routine account and all of its data.
          </div>
        </div>
        <BaseButton
          kind="main"
          type="secondary"
          tint="red"
          text="Delete account"
          @click="() => (showDeleteAccountModal = 'displayed')"
        />
      </div>
    </section>

    <CollapsibleSection v-if="isDev" header="Reset" :hide="true">
      <section class="admin-buttons">
        <BaseButton
          kind="main"
          type="secondary"
          text="Onboarding"
          @click="resetOnboarding"
        />
        <BaseButton
          kind="main"
          type="secondary"
          text="Tutorials (individual)"
          @click="resetTutorialsIndividual"
        />
        <BaseButton
          kind="main"
          type="secondary"
          text="Tutorials (team)"
          @click="resetTutorialsTeam"
        />
      </section>
    </CollapsibleSection>

    <CollapsibleSection v-if="isDev" header="AI Demo" :hide="true">
      <section class="ai-demo-section">
        <div class="ai-demo-description">
          The demo requires four integrations. Your
          <strong>Slack</strong> integration needs a
          <code>#test-integration-app-bot</code> channel that the agent will
          post the meeting agenda to. Your <strong>GitHub</strong> account needs
          to be a member of <code>routineco</code>, and the automation will
          create an issue in <code>routineco/acme-app</code>. You also need to
          set up a data collection for GitHub issues in the
          <code>acme-app</code> repo. When you click the button, a new "Demo
          Dashboard" page will be pinned in your menu. Click it to get started
          with the demo.
        </div>

        <div class="ai-demo-selectors">
          <div class="selector-group">
            <label>Slack Connection</label>
            <BaseInputSelect
              v-model:value="selectedSlackConnectionId"
              :options="slackOptions"
              placeholder="-- Select Slack Connection --"
              :select-only="true"
            />
          </div>

          <div class="selector-group">
            <label>Gmail Connection</label>
            <BaseInputSelect
              v-model:value="selectedGmailConnectionId"
              :options="gmailOptions"
              placeholder="-- Select Gmail Connection --"
              :select-only="true"
            />
          </div>

          <div class="selector-group">
            <label>GitHub Connection</label>
            <BaseInputSelect
              v-model:value="selectedGithubConnectionId"
              :options="githubOptions"
              placeholder="-- Select GitHub Connection --"
              :select-only="true"
            />
          </div>

          <div class="selector-group">
            <label>Calendar</label>
            <BaseInputSelect
              v-model:value="selectedCalendarId"
              :options="calendarOptions"
              placeholder="-- Select Calendar --"
              :select-only="true"
            />
          </div>

          <div class="selector-group">
            <label>GitHub Issues Data Collection</label>
            <BaseInputSelect
              v-model:value="selectedGithubIssuesTableId"
              :options="databaseOptions"
              placeholder="-- Select Data Collection --"
              :select-only="true"
            />
          </div>

          <div class="selector-group">
            <label>Gmail Inbox Data Collection</label>
            <BaseInputSelect
              v-model:value="selectedGmailInboxTableId"
              :options="databaseOptions"
              placeholder="-- Select Data Collection --"
              :select-only="true"
            />
          </div>
        </div>

        <BaseButton
          kind="main"
          text="Setup AI Demo"
          :state="canSetupDemo ? 'default' : 'disabled'"
          @click="setupAiDemo"
        />
      </section>
    </CollapsibleSection>

    <Teleport to="#modal">
      <ModalDeleteAccount
        :status="showDeleteAccountModal"
        @close="() => (showDeleteAccountModal = 'hidden')"
        @delete-account="deleteAccount()"
      />
    </Teleport>
  </div>
</template>
<script lang="ts">
import { computedAsync } from "@vueuse/core";
import { Component, computed, defineComponent, ref } from "vue";

import controller from "@ctrl/controller";
import type { Environment, ProfileKind } from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect from "@/components/base/BaseInputSelect.vue";
import CheckUpdatesButton from "@/components/elements/CheckUpdatesButton.vue";
import CollapsibleSection from "@/components/graph/definition/form/CollapsibleSection.vue";
import GoogleIcon from "@/components/icons/apps/google/GoogleIcon.vue";
import EmailIcon from "@/components/icons/EmailIcon.vue";
import * as inject from "@/components/inject";
import ModalDeleteAccount from "@/components/ModalDeleteAccount.vue";
import { browser } from "@/services/browser";

import { OnboardingUseCase, useOnboarding } from "../onboarding/index";

const { version } = controller;

const SIGN_IN_METHODS: Record<
  ProfileKind["provider"],
  { label: string; icon: Component }
> = {
  email: { label: "Email", icon: EmailIcon },
  google: { label: "Google", icon: GoogleIcon },
};

export default defineComponent({
  components: {
    BaseButton,
    BaseInputSelect,
    CheckUpdatesButton,
    CollapsibleSection,
    ModalDeleteAccount,
  },
  async setup() {
    const controller = inject.controller();
    const logout = inject.logout();
    const menu = inject.menu();
    const profile = inject.profile();
    const integrationsStore = inject.integrations();

    // computedAsync must be before any await to ensure proper cleanup on unmount
    //FIXME: Remove when the back end to update controller.d.ts
    const timezone = computedAsync(async () => await controller.timezone());
    const arch = computedAsync(async () => controller.arch());

    const organizationId = computed(
      () => profile.value?.user.personal.organizationId
    );
    const integrations = integrationsStore.forOrganization(organizationId);

    const email = computed(() => profile.value.user.email);
    const signInMethod = computed(() => {
      const { provider } = profile.value.user;
      return provider ? SIGN_IN_METHODS[provider] : null;
    });

    const slackConnections = computed(() =>
      integrations.connectionsWithMeta.value.filter(
        (c) => c.integrationKey === "slack"
      )
    );
    const gmailConnections = computed(() =>
      integrations.connectionsWithMeta.value.filter(
        (c) => c.integrationKey === "gmail"
      )
    );
    const githubConnections = computed(() =>
      integrations.connectionsWithMeta.value.filter(
        (c) => c.integrationKey === "github"
      )
    );

    const slackOptions = computed(() =>
      slackConnections.value.map((conn) => ({
        value: conn.id,
        label: conn.connectionName || conn.integrationName || conn.id,
        aliases: [],
      }))
    );
    const gmailOptions = computed(() =>
      gmailConnections.value.map((conn) => ({
        value: conn.id,
        label: conn.connectionName || conn.integrationName || conn.id,
        aliases: [],
      }))
    );
    const githubOptions = computed(() =>
      githubConnections.value.map((conn) => ({
        value: conn.id,
        label: conn.connectionName || conn.integrationName || conn.id,
        aliases: [],
      }))
    );

    const selectedSlackConnectionId = ref<string>("");
    const selectedGmailConnectionId = ref<string>("");
    const selectedGithubConnectionId = ref<string>("");
    const selectedCalendarId = ref<string>("");
    const selectedGithubIssuesTableId = ref<string>("");
    const selectedGmailInboxTableId = ref<string>("");

    const calendars = (await controller.calendar.all()).release() ?? [];

    const allDatabasesRef = controller.graph.database.list();
    const allDatabasesDict = (await allDatabasesRef).release() ?? {};
    const allDatabases = Object.values(allDatabasesDict).flatMap((byTable) =>
      Object.values(byTable)
    );

    const calendarOptions = calendars.map((cal) => ({
      value: cal.id,
      label: cal.summary || cal.id,
      aliases: [],
    }));
    const databaseOptions = allDatabases.map((db) => ({
      value: db.id,
      label: db.name,
      aliases: [],
    }));

    const canSetupDemo = computed(
      () =>
        selectedSlackConnectionId.value &&
        selectedGmailConnectionId.value &&
        selectedGithubConnectionId.value &&
        selectedCalendarId.value &&
        selectedGithubIssuesTableId.value &&
        selectedGmailInboxTableId.value
    );

    const setupAiDemo = async () => {
      if (!canSetupDemo.value || !organizationId.value) return;
      try {
        await controller.ai.setupDemo(
          organizationId.value,
          selectedSlackConnectionId.value,
          selectedGmailConnectionId.value,
          selectedGithubConnectionId.value,
          selectedGithubIssuesTableId.value,
          selectedGmailInboxTableId.value,
          selectedCalendarId.value
        );
        controller.notifications.notify("AI demo setup complete");
      } catch (error) {
        controller.notifications.notify(
          `Error setting up AI demo: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    };

    const channel = ref<Environment>();
    void controller.updateChannel().then((value) => {
      channel.value = value;
    });
    const showDeleteAccountModal = ref<"hidden" | "displayed" | "in-progress">(
      "hidden"
    );
    const deleteAccount = async () => {
      showDeleteAccountModal.value = "in-progress";
      await controller.auth.deleteAccount();
      showDeleteAccountModal.value = "hidden";
      logout();
    };
    const copyToClipboard = async (string: string) => {
      await copy(string);
      controller.notifications.notify(`${string} copied to clipboard`);
    };

    const isDev = await controller.dev();

    const resetOnboarding = async () => {
      browser.store.setRaw("forceOnboarding", "true");
      controller.notifications.notify(
        "Onboarding flag has been reset. Reloading app..."
      );
      // Reload the page after a short delay to show the notification
      setTimeout(() => {
        window.location.reload();
      }, 500);
    };

    const resetTutorials = (useCase: OnboardingUseCase) =>
      useOnboarding(controller, profile.value, menu, useCase);

    const resetTutorialsIndividual = () =>
      resetTutorials({ type: "individual" });

    const resetTutorialsTeam = () =>
      resetTutorials({
        type: "team",
        organizationId: profile.value.organizations[0]?.id ?? "",
      });

    return {
      allDatabases,
      arch,
      calendarOptions,
      calendars,
      canSetupDemo,
      databaseOptions,
      channel,
      copyToClipboard,
      deleteAccount,
      email,
      isDev,
      githubOptions,
      gmailConnections,
      gmailOptions,
      githubConnections,
      resetOnboarding,
      selectedCalendarId,
      selectedGithubConnectionId,
      selectedGithubIssuesTableId,
      selectedGmailConnectionId,
      selectedGmailInboxTableId,
      selectedSlackConnectionId,
      setupAiDemo,
      showDeleteAccountModal,
      signInMethod,
      slackConnections,
      slackOptions,
      timezone,
      resetTutorialsIndividual,
      resetTutorialsTeam,
      version,
    };
  },
});
</script>
<style scoped lang="scss">
@use "../../styles/settings.scss";

.sign-in-method,
.version {
  display: flex;
  align-items: center;
}

.sign-in-method {
  column-gap: 6px;

  svg {
    width: 14px;
    height: 14px;
  }
}

.version {
  column-gap: 12px;
}

.admin-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 12px;
}

.ai-demo-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-demo-description {
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-50);
  }
}

.ai-demo-selectors {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.selector-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    @extend %font-caption-medium;
    @include themed() {
      color: t($grey-80);
    }
  }
}
</style>
